import type { Theme } from "@mui/material";

export type MobileNavBadgeStatus = "active" | "failed" | "pending";

export type MobileNavBadgeColor = "error" | "info" | "warning";

export interface MobileNavBadge {
	count?: number;
	status: MobileNavBadgeStatus;
}

interface MobileNavSlotBase {
	badge?: MobileNavBadge;
	disabled?: boolean;
	icon: string;
	id: string;
	label: string;
}

export interface MobileNavRouteSlot extends MobileNavSlotBase {
	segment: string;
}

export interface MobileNavPopupTrigger {
	controls?: string;
	expanded: boolean;
	triggerId: string;
}

export interface MobileNavActionSlot extends MobileNavSlotBase {
	onAction: (trigger: HTMLElement) => void;
	popup?: MobileNavPopupTrigger;
}

export type MobileNavSlot = MobileNavActionSlot | MobileNavRouteSlot;

export interface MobileNavPromotedAction {
	badge?: MobileNavBadge;
	busy?: boolean;
	busyIcon?: string;
	busyLabel?: string;
	count?: number;
	disabled?: boolean;
	hidden?: boolean;
	icon: string;
	label: string;
	onAction: () => void;
}

export const MOBILE_NAV_MIN_TOUCH_TARGET_PX = 48;

export const PHONE_TOUCH_TARGET_SX = (theme: Theme) => ({
	[theme.breakpoints.down("md")]: { minHeight: MOBILE_NAV_MIN_TOUCH_TARGET_PX }
});

export const PHONE_SQUARE_TOUCH_TARGET_SX = (theme: Theme) => ({
	[theme.breakpoints.down("md")]: {
		minHeight: MOBILE_NAV_MIN_TOUCH_TARGET_PX,
		minWidth: MOBILE_NAV_MIN_TOUCH_TARGET_PX
	}
});

export const calculatePhoneTouchTargetInsetPx = (facePx: number) => (MOBILE_NAV_MIN_TOUCH_TARGET_PX - facePx) / 2;

export const buildPhoneTouchTargetAroundFaceSx = (facePx: number, faceRadius?: number) => {
	const insetPx = calculatePhoneTouchTargetInsetPx(facePx);
	return (theme: Theme) => ({
		[theme.breakpoints.down("md")]: {
			backgroundClip: "content-box",
			...(faceRadius !== undefined && {
				borderRadius: `calc(${theme.shape.borderRadius}px * ${faceRadius} + ${insetPx}px)`
			}),
			margin: `-${insetPx}px`,
			minHeight: MOBILE_NAV_MIN_TOUCH_TARGET_PX,
			minWidth: MOBILE_NAV_MIN_TOUCH_TARGET_PX,
			padding: `${insetPx}px`
		}
	});
};

export const buildPhoneTouchTargetInSlotSx = (slotPx: number) => {
	const overhangPx = (MOBILE_NAV_MIN_TOUCH_TARGET_PX - slotPx) / 2;
	return (theme: Theme) => ({
		[theme.breakpoints.down("md")]: {
			marginBottom: `-${overhangPx}px`,
			marginTop: `-${overhangPx}px`,
			minHeight: MOBILE_NAV_MIN_TOUCH_TARGET_PX
		}
	});
};

const AUTOCOMPLETE_INDICATOR_TEXT_GAP_PX = 4;

export const PHONE_AUTOCOMPLETE_INDICATOR_TOUCH_TARGET_SX = (theme: Theme) => ({
	[theme.breakpoints.down("md")]: {
		"& .MuiAutocomplete-root": {
			"& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator": {
				marginRight: 0,
				minHeight: MOBILE_NAV_MIN_TOUCH_TARGET_PX,
				minWidth: MOBILE_NAV_MIN_TOUCH_TARGET_PX
			},
			"& .MuiOutlinedInput-root .MuiAutocomplete-endAdornment": { right: 0 },
			"&.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root, &.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root": {
				paddingRight: `${MOBILE_NAV_MIN_TOUCH_TARGET_PX + AUTOCOMPLETE_INDICATOR_TEXT_GAP_PX}px`
			},
			"&.MuiAutocomplete-hasClearIcon.MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root": {
				paddingRight: `${2 * MOBILE_NAV_MIN_TOUCH_TARGET_PX + AUTOCOMPLETE_INDICATOR_TEXT_GAP_PX}px`
			}
		}
	}
});

const SMALL_CHIP_HEIGHT_PX = 24;

const SMALL_CHIP_DELETE_ICON_PX = 16;

const SMALL_CHIP_DELETE_ICON_OVERLAP_PX = 4;

const SMALL_CHIP_DELETE_INSET_PX = calculatePhoneTouchTargetInsetPx(SMALL_CHIP_DELETE_ICON_PX);

const SMALL_CHIP_DELETE_LABEL_CLEARANCE_PX = SMALL_CHIP_DELETE_ICON_OVERLAP_PX + SMALL_CHIP_DELETE_INSET_PX;

export const PHONE_SMALL_CHIP_DELETE_TOUCH_TARGET_SX = (theme: Theme) => ({
	[theme.breakpoints.down("md")]: {
		"&.MuiChip-clickable .MuiChip-label": { paddingRight: `${SMALL_CHIP_DELETE_LABEL_CLEARANCE_PX}px` },
		"& .MuiChip-deleteIcon": {
			boxSizing: "content-box",
			marginBottom: `-${SMALL_CHIP_DELETE_INSET_PX}px`,
			marginLeft: `-${SMALL_CHIP_DELETE_LABEL_CLEARANCE_PX}px`,
			marginRight: 0,
			marginTop: `-${SMALL_CHIP_DELETE_INSET_PX}px`,
			padding: `${SMALL_CHIP_DELETE_INSET_PX}px`
		}
	}
});

export const PHONE_SMALL_CHIP_DELETE_ROW_SX = (theme: Theme) => ({
	[theme.breakpoints.down("md")]: { rowGap: `${MOBILE_NAV_MIN_TOUCH_TARGET_PX - SMALL_CHIP_HEIGHT_PX}px` }
});

export const MOBILE_NAV_PROMOTED_GUTTER = 0.5;

export const MOBILE_NAV_BADGE_MAX = 99;

export const MOBILE_NAV_BADGE_COLOR: Record<MobileNavBadgeStatus, MobileNavBadgeColor> = {
	active: "info",
	failed: "error",
	pending: "warning"
};

const MOBILE_NAV_BADGE_PRIORITY: Record<MobileNavBadgeStatus, number> = {
	active: 2,
	failed: 3,
	pending: 1
};

export type MobileNavBadgeCandidate = MobileNavBadge | false | null | undefined;

const hasVisibleCount = (badge: MobileNavBadge): boolean => badge.count === undefined || badge.count > 0;

export const resolveNavBadge = (candidates: readonly MobileNavBadgeCandidate[]): MobileNavBadge | undefined =>
	candidates.reduce<MobileNavBadge | undefined>((winner, candidate) => {
		if (!candidate || !hasVisibleCount(candidate)) return winner;
		if (!winner) return candidate;
		return MOBILE_NAV_BADGE_PRIORITY[candidate.status] > MOBILE_NAV_BADGE_PRIORITY[winner.status] ? candidate : winner;
	}, undefined);

export const isMobileNavRouteSlot = (slot: MobileNavSlot): slot is MobileNavRouteSlot => "segment" in slot;

const trimSlashes = (value: string): string => value.replace(/^\/+|\/+$/g, "");

export const resolveNavSlotPath = (basePath: string, segment: string): string => {
	const base = trimSlashes(basePath);
	const relative = trimSlashes(segment);
	const joined = [base, relative].filter(Boolean).join("/");
	return `/${joined}`;
};

const isPathWithin = (pathname: string, candidatePath: string): boolean =>
	candidatePath === "/" || pathname === candidatePath || pathname.startsWith(`${candidatePath}/`);

export const resolveNavNamespace = (pathname: string, namespaces: readonly [string, ...string[]]): string =>
	namespaces.find((namespace) => isPathWithin(pathname, resolveNavSlotPath(namespace, ""))) ?? namespaces[0];

export const resolvePromotedNavIndex = (slotCount: number): number => Math.floor((slotCount + 1) / 2);

const resolveActiveNavSlotId = (
	pathname: string,
	basePath: string,
	slots: readonly MobileNavSlot[]
): string | undefined =>
	slots
		.filter(isMobileNavRouteSlot)
		.map((slot) => ({ id: slot.id, path: resolveNavSlotPath(basePath, slot.segment) }))
		.filter((candidate) => isPathWithin(pathname, candidate.path))
		.sort((first, second) => second.path.length - first.path.length)[0]?.id;

export default resolveActiveNavSlotId;
